import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/apiClient';

export const useOffers = () => {
  return useQuery({
    queryKey: ['offers'],
    queryFn: async () => {
      const data = await apiClient.get('/offers');
      return Array.isArray(data) ? data : [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 10 * 60 * 1000, // Background refresh every 10 minutes
    refetchIntervalInBackground: true,
  });
};

export const useOffersWithEnrollments = () => {
  return useQuery({
    queryKey: ['offers', 'withEnrollments'],
    queryFn: async () => {
      // Fetch offers and enrollment counts in parallel
      const [offers, enrollmentCounts] = await Promise.all([
        apiClient.get('/offers'),
        apiClient.get('/enrollments/counts')
      ]);
      
      const offersArray = Array.isArray(offers) ? offers : [];
      const countsArray = Array.isArray(enrollmentCounts) ? enrollmentCounts : [];
      
      // Create a map of offer_id to counts for quick lookup
      const countsMap = new Map();
      countsArray.forEach(count => {
        countsMap.set(count.offer_id, count);
      });
      
      // Add enrollment counts to each offer
      return offersArray.map(offer => {
        const counts = countsMap.get(offer.id) || {
          active_count: 0,
          completed_count: 0,
          total_count: 0
        };
        
        return {
          ...offer,
          activeEnrollmentCount: counts.active_count,
          completedEnrollmentCount: counts.completed_count,
          totalEnrollmentCount: counts.total_count
        };
      });
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 10 * 60 * 1000, // Background refresh every 10 minutes
    refetchIntervalInBackground: true,
  });
};

// Background preloader hook to load data immediately
export const usePreloadData = () => {
  // This will run immediately when the app starts
  const offersQuery = useOffers();
  const offersWithEnrollmentsQuery = useOffersWithEnrollments();
  
  return {
    offersQuery,
    offersWithEnrollmentsQuery,
  };
};

// Progressive loading hooks for offer dialog

// Fetch basic offer details (fast - no heavy operations)
export const useOfferDetails = (offerId: string | null) => {
  return useQuery({
    queryKey: ['offer', offerId],
    queryFn: async () => {
      if (!offerId) return null;
      return await apiClient.get(`/offers/${offerId}`);
    },
    enabled: !!offerId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Fetch offer tasks separately
export const useOfferTasks = (offerId: string | null) => {
  return useQuery({
    queryKey: ['offer', offerId, 'tasks'],
    queryFn: async () => {
      if (!offerId) return null;
      return await apiClient.get(`/tasks/offer/${offerId}`);
    },
    enabled: !!offerId,
    staleTime: 2 * 60 * 1000, // 2 minutes - tasks change more frequently
  });
};

// Fetch offer enrollments separately
export const useOfferEnrollments = (offerId: string | null) => {
  return useQuery({
    queryKey: ['offer', offerId, 'enrollments'],
    queryFn: async () => {
      if (!offerId) return null;
      return await apiClient.get(`/enrollments/offer/${offerId}`);
    },
    enabled: !!offerId,
    staleTime: 1 * 60 * 1000, // 1 minute - enrollments change frequently
  });
};

// Fetch offer referral items separately
export const useOfferReferralItems = (offerId: string | null) => {
  return useQuery({
    queryKey: ['offer', offerId, 'referrals'],
    queryFn: async () => {
      if (!offerId) return null;
      const offer = await apiClient.get(`/offers/${offerId}`) as any;
      return offer?.referral_items || [];
    },
    enabled: !!offerId,
    staleTime: 5 * 60 * 1000, // 5 minutes - referral items don't change often
  });
};

// Mutations for offer management
export const useUpdateOffer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, offer }: { id: string; offer: any }) => {
      return await apiClient.put(`/offers/${id}`, offer);
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      queryClient.invalidateQueries({ queryKey: ['offer', id] });
    },
  });
};

export const useDeleteOffer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (offerId: string) => {
      return await apiClient.delete(`/offers/${offerId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offers'] });
    },
  });
};

// Task management mutations
export const useCreateOfferTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ offerId, task }: { offerId: string; task: any }) => {
      return await apiClient.post(`/tasks/offer/${offerId}`, task);
    },
    onSuccess: (_, { offerId }) => {
      queryClient.invalidateQueries({ queryKey: ['offer', offerId, 'tasks'] });
    },
  });
};

export const useUpdateTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, task }: { taskId: number; task: any }) => {
      return await apiClient.put(`/tasks/${taskId}`, task);
    },
    onSuccess: (_, { task }) => {
      if (task.offer_id) {
        queryClient.invalidateQueries({ queryKey: ['offer', task.offer_id, 'tasks'] });
      }
    },
  });
};

export const useDeleteTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (taskId: number) => {
      return await apiClient.delete(`/tasks/${taskId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

// Referral item management mutations
export const useAddReferralItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ offerId, referralItem }: { offerId: string; referralItem: any }) => {
      return await apiClient.post(`/offers/${offerId}/referral-items`, referralItem);
    },
    onSuccess: (_, { offerId }) => {
      queryClient.invalidateQueries({ queryKey: ['offer', offerId, 'referrals'] });
      queryClient.invalidateQueries({ queryKey: ['offer', offerId] });
    },
  });
};

export const useUpdateReferralItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ offerId, referralItemId, referralItem }: { offerId: string; referralItemId: string; referralItem: any }) => {
      return await apiClient.put(`/offers/${offerId}/referral-items/${referralItemId}`, referralItem);
    },
    onSuccess: (_, { offerId }) => {
      queryClient.invalidateQueries({ queryKey: ['offer', offerId, 'referrals'] });
      queryClient.invalidateQueries({ queryKey: ['offer', offerId] });
    },
  });
};

export const useDeleteReferralItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ offerId, referralItemId }: { offerId: string; referralItemId: string }) => {
      return await apiClient.delete(`/offers/${offerId}/referral-items/${referralItemId}`);
    },
    onSuccess: (_, { offerId }) => {
      queryClient.invalidateQueries({ queryKey: ['offer', offerId, 'referrals'] });
      queryClient.invalidateQueries({ queryKey: ['offer', offerId] });
    },
  });
};

// Enrollment management mutations
export const useCreateEnrollment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, offerId, referralItemId }: { userId: number; offerId: string; referralItemId?: string }) => {
      console.log('useCreateEnrollment - Starting enrollment creation:', { userId, offerId, referralItemId });
      
      try {
        const params = new URLSearchParams();
        params.append('user_id', userId.toString());
        params.append('offer_id', offerId);
        if (referralItemId) {
          params.append('referral_item_id', referralItemId);
        }
        
        const url = `/enrollments?${params.toString()}`;
        console.log('useCreateEnrollment - Making API call to:', url);
        
        const result = await apiClient.post(url);
        console.log('useCreateEnrollment - API call successful, result:', result);
        
        return result;
      } catch (error) {
        console.error('useCreateEnrollment - API call failed:', error);
        
        // Log detailed error information
        if (error && typeof error === 'object') {
          console.error('useCreateEnrollment - Error details:', {
            message: 'message' in error ? error.message : 'No message',
            stack: 'stack' in error ? error.stack : 'No stack',
            response: 'response' in error ? error.response : 'No response'
          });
        }
        
        throw error;
      }
    },
    onSuccess: (result, variables) => {
      console.log('useCreateEnrollment - onSuccess called with:', { result, variables });
      
      try {
        const { offerId } = variables;
        console.log('useCreateEnrollment - Invalidating queries for offerId:', offerId);
        
        queryClient.invalidateQueries({ queryKey: ['offer', offerId, 'enrollments'] });
        queryClient.invalidateQueries({ queryKey: ['offers'] });
        
        console.log('useCreateEnrollment - Query invalidation completed successfully');
      } catch (error) {
        console.error('useCreateEnrollment - Error in onSuccess callback:', error);
        throw error;
      }
    },
    onError: (error, variables) => {
      console.error('useCreateEnrollment - onError called:', { error, variables });
    }
  });
};

export const useDeleteEnrollment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (enrollmentId: number) => {
      return await apiClient.delete(`/enrollments/${enrollmentId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrollments'] });
      // Force immediate refetch of offers to update referral usage counts
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      queryClient.refetchQueries({ queryKey: ['offers'] });
      // Also invalidate all enrollments query for dynamic usage calculation
      queryClient.invalidateQueries({ queryKey: ['enrollments', 'all'] });
      queryClient.refetchQueries({ queryKey: ['enrollments', 'all'] });
    },
  });
};

// Enrollment task management hooks
export const useEnrollmentCompletions = (enrollmentId: number | null) => {
  return useQuery({
    queryKey: ['enrollments', enrollmentId, 'completions'],
    queryFn: async () => {
      if (!enrollmentId) return [];
      return await apiClient.get(`/tasks/enrollment/${enrollmentId}/completions`);
    },
    enabled: !!enrollmentId,
  });
};

export const useMarkTaskCompleted = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ enrollmentId, taskId, notes }: { enrollmentId: number; taskId: number; notes?: string }) => {
      return await apiClient.post(`/tasks/enrollment/${enrollmentId}/task/${taskId}/complete`, {
        notes: notes || null,
        selected_referral_id: null
      });
    },
    onSuccess: (_, { enrollmentId }) => {
      queryClient.invalidateQueries({ queryKey: ['enrollments', enrollmentId, 'completions'] });
      queryClient.invalidateQueries({ queryKey: ['enrollments'] });
    },
  });
};

export const useMarkTaskUncompleted = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ enrollmentId, taskId }: { enrollmentId: number; taskId: number }) => {
      return await apiClient.post(`/tasks/enrollment/${enrollmentId}/task/${taskId}/uncomplete`);
    },
    onSuccess: (_, { enrollmentId }) => {
      queryClient.invalidateQueries({ queryKey: ['enrollments', enrollmentId, 'completions'] });
      queryClient.invalidateQueries({ queryKey: ['enrollments'] });
    },
  });
};

export const useEnrollmentTasksWithFees = (enrollmentId: number | null) => {
  return useQuery({
    queryKey: ['enrollments', enrollmentId, 'tasks'],
    queryFn: async () => {
      if (!enrollmentId) return [];
      return await apiClient.get(`/tasks/enrollment/${enrollmentId}/with-fees`);
    },
    enabled: !!enrollmentId,
  });
};

// User management for enrollments
export const useUsers = () => {
  return useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      return await apiClient.get('/users');
    },
  });
};
